<template>
  <div class="theme-autocomplete__field-holder pdok-address-typeahead">
    <!--
      .theme-autocomplete establishes position:relative - required for the list's own
      position:absolute (top:100%) to anchor directly below this component instead of falling
      back to a distant positioned ancestor (or none), which is what previously made the list
      render as a plain in-flow block pushing the rest of the form down instead of floating.
      Matches ThemeAutocomplete.vue's own wrapping structure exactly.
    -->
    <div class="theme-autocomplete">
      <ul
        v-if="showList && suggestions.length"
        class="theme-autocomplete__list"
        role="listbox"
        :aria-label="labels.listboxLabel"
      >
        <li
          v-for="(candidate, index) in suggestions"
          :key="candidate.id"
          :id="optionId(index)"
          class="theme-autocomplete__item"
          role="option"
          :aria-selected="index === highlightedIndex ? 'true' : 'false'"
          :class="{ 'theme-autocomplete__item--is-selected': index === highlightedIndex }"
          @mousedown.prevent="selectCandidate(candidate)"
          @touchstart.prevent="selectCandidate(candidate)"
        >
          {{ candidate.label }}
        </li>
      </ul>
    </div>
    <div v-if="message" class="text-sm mt-1" style="color: red" role="status" aria-live="polite">
      {{ message }}
    </div>
  </div>
</template>

<script>
function csrfToken() {
  var meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

var MESSAGES = {
  nl: {
    listboxLabel: 'Adressuggesties',
    unavailable: 'Adressuggesties zijn tijdelijk niet beschikbaar; voer het adres handmatig in.',
    noResults: 'Geen adressen gevonden.',
    invalidSelection: 'Deze suggestie kon niet worden verwerkt; voer het adres handmatig in.',
  },
  en: {
    listboxLabel: 'Address suggestions',
    unavailable: 'Address suggestions are temporarily unavailable; enter the address manually.',
    noResults: 'No matching addresses found.',
    invalidSelection: 'This suggestion could not be resolved; enter the address manually.',
  },
};

// Debounce delay and the minimum combined-input length before a Suggest call is worth making at
// all - both deliberately conservative so a typeahead never fires on every single keystroke of a
// postcode that is still obviously incomplete (e.g. "10").
var DEBOUNCE_MS = 350;
var MIN_POSTCODE_LENGTH = 6;
// The parent's address inputs this component works with (the keyboard and blur handling read them by name).
var LOOKUP_FIELD_NAMES = ['postcode', 'building_uid', 'building_uid_appendix'];

function isLookupField(element) {
  var name = element && element.getAttribute ? element.getAttribute('name') : null;
  return LOOKUP_FIELD_NAMES.indexOf(name) !== -1;
}

// House number and addition of a suggestion label, from the end of its first part: "Dam 12A",
// "Kalverstraat 49-1", "Kalverstraat 159A-2" (PDOK weergavenaam). Null when the label has another form.
function labelHouseNumber(label) {
  var head = String(label || '').split(',')[0].trim();
  var match = head.match(/\s(\d+)([A-Za-z]?)(?:-([A-Za-z0-9]{1,4}))?$/);
  return match ? { number: match[1], rest: (match[2] + (match[3] || '')).toUpperCase() } : null;
}

// The typed house number and addition in the same shape ("12" + "a" or "12a" -> 12 / A; "159" + "2"
// -> 159 / 2). The number comes from the house number field only, so a numeric addition stays apart.
function typedHouseNumber(houseNumber, addition) {
  var match = String(houseNumber || '').replace(/\s+/g, '').match(/^(\d+)(.*)$/);
  return match ? { number: match[1], rest: (match[2] + String(addition || '')).replace(/[\s-]+/g, '').toUpperCase() } : null;
}

// The postcode of a suggestion label ("Dam 1, 1012JS Amsterdam" -> 1012JS), or null.
function labelPostcode(label) {
  var tail = String(label || '').split(',')[1];
  var match = tail ? tail.trim().match(/^(\d{4})\s?([A-Za-z]{2})\b/) : null;
  return match ? (match[1] + match[2]).toUpperCase() : null;
}

// Exactly the typed number as a plain house: one suggestion with the typed number and no addition, in
// the typed postcode, and no other suggestion with that same number ("95" next to "95A" or "95-1": no;
// "95" next to "951" or "953": yes, PDOK matches the number as a prefix and those are other houses). A
// label of another form, or the typed number in another postcode, counts as a possible other address.
// With an addition typed, or no plain number, never. Only the five rows the lookup returns count.
function isExactPlainHouse(suggestions, houseNumber, addition, postcode) {
  if (String(addition || '').trim() !== '') return false;
  var typed = typedHouseNumber(houseNumber, '');
  if (!typed || typed.rest !== '') return false;
  var plain = 0;
  for (var i = 0; i < suggestions.length; i++) {
    var label = labelHouseNumber(suggestions[i].label);
    if (!label) return false;
    if (label.number !== typed.number) continue;
    if (label.rest !== '') return false;
    if (labelPostcode(suggestions[i].label) !== postcode) return false;
    plain += 1;
  }
  return plain === 1;
}

export default {
  name: 'PdokAddressTypeahead',
  props: {
    postcode: { type: String, default: '' },
    houseNumber: { type: String, default: '' },
    addition: { type: String, default: '' },
    suggestUrl: { type: String, required: true },
    resolveUrl: { type: String, required: true },
    // Postcode-only mode (street/city, no house number - see AddressEnrichmentService::
    // suggestPostcode()/resolvePostcode()). Optional so an older caller that hasn't been updated
    // yet still works exactly as before: without these, the component simply never activates
    // postcode-only mode and behaves identically to its pre-existing postcode+house-number-only
    // contract.
    suggestPostcodeUrl: { type: String, default: '' },
    resolvePostcodeUrl: { type: String, default: '' },
    locale: { type: String, default: 'nl' },
    // The three options below are off by default, so add-house, the property edit form and the
    // manage form keep their behaviour; the Vuuro let wizard turns them on.
    // commitOnBlur: leaving the address fields (Tab or a click elsewhere, not moving to another
    // address field) while there are suggestions picks the best match, and suggestions that arrive
    // after the user left the fields are picked the same way, instead of leaving a list nobody sees.
    commitOnBlur: { type: Boolean, default: false },
    // quiet: no lookup on mount and none on a field change while true, for a resumed form whose
    // address is already complete; the parent turns it off once the user edits an address field.
    quiet: { type: Boolean, default: false },
    // addressFilled: the parent already has a usable address (street and city), so a suggestion
    // that cannot be resolved shows no "enter the address manually" message.
    addressFilled: { type: Boolean, default: false },
  },
  data() {
    return {
      suggestions: [],
      highlightedIndex: -1,
      showList: false,
      message: null,
      selectedPdokId: null,
      // Which flow the CURRENT suggestions/selection belongs to - 'address' (postcode + house
      // number, exact-address resolve, may trigger BAG/energy-label enrichment downstream) or
      // 'postcode' (postcode alone, street/city only, never enrichment). Selecting a candidate
      // must resolve through - and only through - the endpoint/event pair matching this mode.
      activeMode: null,
      requestToken: 0,
      lastSentQuery: null,
      debounceTimer: null,
      // commitOnBlur bookkeeping: a lookup is waiting in the debounce; the query and mode the shown
      // suggestions belong to; a pick made on leaving the fields that is still on its way.
      debouncePending: false,
      listKey: null,
      listMode: null,
      settling: false,
      settlingTimer: null,
      // commitOnBlur: bumped for every resolve and when the guest types again, so only the newest
      // pick lands.
      resolveToken: 0,
      // The element last pressed with a mouse or finger, and when (a click on a marked control).
      pressedTarget: null,
      pressedAt: 0,
      abortController: null,
      // commitOnBlur: the guest tabbed from the house number into an empty addition while the list was
      // still on its way; that answer commits exactly the typed plain house when it lands.
      plainHouseOnArrival: false,
      applyingFill: false,
      instanceId: 'pdok-typeahead-' + Math.random().toString(36).slice(2),
    };
  },
  computed: {
    labels() {
      return MESSAGES[this.locale] || MESSAGES.nl;
    },
    normalizedPostcode() {
      return (this.postcode || '').replace(/\s+/g, '').toUpperCase();
    },
    combinedAddressQuery() {
      var parts = [this.postcode || '', this.houseNumber || ''];
      var suffix = (this.addition || '').trim();
      var q = parts.join(' ').trim();
      if (suffix) {
        q += ' ' + suffix;
      }
      return q.replace(/\s+/g, ' ').trim();
    },
    // Postcode + house number: exact-address resolution (may trigger BAG/energy-label
    // enrichment downstream via the `resolved` event's payload).
    hasEnoughInputForAddress() {
      return this.normalizedPostcode.length >= MIN_POSTCODE_LENGTH
        && (this.houseNumber || '').trim() !== '';
    },
    // Postcode alone: street/city only. Deliberately mutually exclusive with the address mode
    // above - the moment a house number is present, the exact-address flow takes over instead,
    // matching "keep postcode + house number for exact-address resolution".
    hasEnoughInputForPostcodeOnly() {
      return this.normalizedPostcode.length >= MIN_POSTCODE_LENGTH
        && (this.houseNumber || '').trim() === ''
        && !!this.suggestPostcodeUrl;
    },
  },
  watch: {
    postcode() {
      if (!this.quiet) this.onAddressFieldChanged();
    },
    houseNumber() {
      if (!this.quiet) this.onAddressFieldChanged();
    },
    addition() {
      if (!this.quiet) this.onAddressFieldChanged();
    },
  },
  mounted() {
    this._onDocumentKeydown = this.onDocumentKeydown.bind(this);
    document.addEventListener('keydown', this._onDocumentKeydown, true);
    this._onDocumentFocusout = this.onDocumentFocusout.bind(this);
    document.addEventListener('focusout', this._onDocumentFocusout, true);
    this._onDocumentPress = this.onDocumentPress.bind(this);
    document.addEventListener('mousedown', this._onDocumentPress, true);
    document.addEventListener('touchstart', this._onDocumentPress, true);
    // Prop watchers only fire on a subsequent CHANGE, never for the initial mount value - without
    // this, a page that loads with an already-filled draft address (postcode/house number
    // restored from a previous session) would never get suggestions until the user edited a
    // field. Guarded the same way runSuggest() itself is, so an empty/incomplete mount never
    // arms a pointless debounce timer at all. A quiet parent (resumed, complete address) skips it.
    if (!this.quiet && (this.hasEnoughInputForAddress || this.hasEnoughInputForPostcodeOnly)) {
      this.onAddressFieldChanged();
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onDocumentKeydown, true);
    document.removeEventListener('focusout', this._onDocumentFocusout, true);
    document.removeEventListener('mousedown', this._onDocumentPress, true);
    document.removeEventListener('touchstart', this._onDocumentPress, true);
    // Whatever was on its way is dropped now (an answer that still lands picks nothing), and the parent
    // hears that the wait is over, so a save it held back for the pick is not held for good.
    this.dropPendingPick();
  },
  methods: {
    optionId(index) {
      return this.instanceId + '-option-' + index;
    },
    markUnavailable() {
      this.suggestions = [];
      this.showList = false;
      this.activeMode = null;
      this.listKey = this.lastSentQuery;
      this.endSettling();
      this.message = this.labels.unavailable;
      // Lets the add-house page fall back to the legacy blur-triggered lookup for the rest of
      // this page load instead of retrying PDOK indefinitely on every keystroke.
      this.$emit('unavailable');
    },
    onAddressFieldChanged() {
      // A field change caused by this component filling in a resolved address must not
      // invalidate the very selection that just produced it.
      if (this.applyingFill) {
        return;
      }
      if (this.selectedPdokId) {
        // The user edited a field after a selection was made - the previously selected PDOK
        // identity no longer describes the current field values, so it must not be retained.
        this.selectedPdokId = null;
      }
      this.message = null;
      if (this.commitOnBlur) {
        // The guest is typing the address again: a search or a pick still on its way is for what was
        // there before. The debounce below asks again for what is there now.
        this.requestToken += 1;
        if (this.abortController) this.abortController.abort();
        this.lastSentQuery = null;
        this.resolveToken += 1;
        this.plainHouseOnArrival = false;
        this.endSettling();
      }
      this.scheduleSuggest();
    },
    scheduleSuggest() {
      clearTimeout(this.debounceTimer);
      this.debouncePending = true;
      this.debounceTimer = setTimeout(this.runSuggest, DEBOUNCE_MS);
    },
    runSuggest() {
      this.debouncePending = false;
      if (this.hasEnoughInputForAddress) {
        this.runAddressSuggest();
        return;
      }
      if (this.hasEnoughInputForPostcodeOnly) {
        this.runPostcodeSuggest();
        return;
      }
      this.suggestions = [];
      this.showList = false;
      this.activeMode = null;
      if (this.commitOnBlur) {
        // An answer still on its way is for a query the fields no longer hold.
        this.requestToken += 1;
        if (this.abortController) this.abortController.abort();
      }
      // Without this, clearing the fields and then re-entering the SAME query would be silently
      // treated as a duplicate of the already-answered query and never re-fetched, even though
      // the dropdown itself was already cleared above.
      this.lastSentQuery = null;
    },
    runAddressSuggest() {
      this.performSuggest('address', 'address:' + this.combinedAddressQuery, this.suggestUrl, { q: this.combinedAddressQuery });
    },
    runPostcodeSuggest() {
      this.performSuggest('postcode', 'postcode:' + this.normalizedPostcode, this.suggestPostcodeUrl, { postcode: this.normalizedPostcode });
    },
    performSuggest(mode, dedupKey, url, body) {
      if (dedupKey === this.lastSentQuery) {
        // Identical normalized query already in flight/answered - never send a duplicate request.
        return;
      }
      this.lastSentQuery = dedupKey;
      this.activeMode = mode;
      // commitOnBlur: the shown list is older than this request, also for the same key (asked again
      // after typing), so leaving the fields now waits for the answer instead of picking from it.
      if (this.commitOnBlur) this.listKey = null;

      if (this.abortController) {
        this.abortController.abort();
      }
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      this.abortController = controller;

      var token = ++this.requestToken;
      var self = this;

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined,
      }).then(function (response) {
        return response.json().then(function (responseBody) {
          return { httpStatus: response.status, body: responseBody };
        });
      }).then(function (result) {
        // A monotonically increasing token means a slow, now-superseded response - from EITHER
        // mode - can never overwrite what a newer request already produced.
        if (token !== self.requestToken) {
          return;
        }
        self.handleSuggestResult(result.httpStatus, result.body, mode, dedupKey);
      }).catch(function (error) {
        if (error && error.name === 'AbortError') {
          return;
        }
        if (token !== self.requestToken) {
          return;
        }
        self.markUnavailable();
      });
    },
    handleSuggestResult(httpStatus, body, mode, key) {
      if (httpStatus !== 200 || !body) {
        this.markUnavailable();
        return;
      }
      if (body.status === 'unavailable') {
        this.markUnavailable();
        return;
      }
      // commitOnBlur: an answer for an address the fields no longer hold is not used at all, also not
      // a single suggestion that would otherwise be picked by itself.
      if (this.commitOnBlur && key !== this.currentQueryKey()) {
        this.showList = false;
        this.endSettling();
        return;
      }
      this.listKey = key;
      this.listMode = mode;
      if (body.status === 'empty' || !Array.isArray(body.data) || body.data.length === 0) {
        this.suggestions = [];
        this.showList = false;
        this.message = this.labels.noResults;
        this.endSettling();
        return;
      }

      this.suggestions = body.data.slice(0, 5);
      this.highlightedIndex = -1;
      this.message = null;
      this.activeMode = mode;

      if (this.suggestions.length === 1) {
        // Resolved to exactly one unambiguous candidate - fill it automatically. Two or more
        // candidates (different house numbers/suffixes for address mode, or different
        // streets/cities sharing one postcode for postcode mode) must never be auto-selected;
        // the list stays open for an explicit choice instead.
        this.showList = false;
        this.selectCandidate(this.suggestions[0]);
        return;
      }

      // commitOnBlur: the user already left the address fields, so nobody would see a list.
      if (this.commitOnBlur && !isLookupField(document.activeElement)) {
        this.plainHouseOnArrival = false;
        this.commitBestMatch();
        return;
      }
      // commitOnBlur: the Tab into an empty addition came before this answer; it commits exactly the
      // typed plain house while the guest is still there with nothing typed. Otherwise the list.
      if (this.plainHouseOnArrival) {
        this.plainHouseOnArrival = false;
        if (this.commitOnBlur && this.isEmptyAdditionField(document.activeElement)
          && mode === 'address' && isExactPlainHouse(this.suggestions, this.houseNumber, this.addition, this.normalizedPostcode)) {
          this.commitBestMatch();
          return;
        }
      }

      this.showList = true;
      this.endSettling();
    },
    onDocumentPress(event) {
      // A right or middle press opens a menu; it is no click on a control.
      if (event.type === 'mousedown' && event.button !== 0) return;
      this.pressedTarget = event.target;
      this.pressedAt = Date.now();
    },
    // The query the fields describe now, in the form of the dedup key ('address:...' or 'postcode:...').
    currentQueryKey() {
      if (this.hasEnoughInputForAddress) {
        return 'address:' + this.combinedAddressQuery;
      }
      if (this.hasEnoughInputForPostcodeOnly) {
        return 'postcode:' + this.normalizedPostcode;
      }
      return null;
    },
    // commitOnBlur only: focus left an address field for something that is not another address
    // field (Tab, a click elsewhere). Only a list for the query the fields hold now is committed: a
    // change still waiting in the debounce is looked up at once, and that answer, like one still on
    // its way, commits itself because focus is out.
    onDocumentFocusout(event) {
      if (!this.commitOnBlur || !isLookupField(event.target)) {
        return;
      }
      var to = event.relatedTarget;
      // Moving on to the next address field is still typing the address, except a Tab from the house
      // number into an empty addition: that commits exactly the typed plain house (VUURO-15).
      if (isLookupField(to)) {
        if (event.target.getAttribute('name') === 'building_uid' && this.isEmptyAdditionField(to)) {
          this.commitPlainHouse();
        }
        return;
      }
      // A control the parent marks (enter the address by hand, edit it) takes over from the lookup when
      // it is clicked or tapped, read from the press itself: Safari and Firefox on a Mac do not focus a
      // clicked button, so relatedTarget is empty there. Nothing is picked, now or when an answer on
      // its way lands. Reached with Tab it is just the next control, so the pick still happens.
      var pressed = this.pressedTarget && this.pressedTarget.closest && Date.now() - this.pressedAt < 1000
        ? this.pressedTarget.closest('[data-address-no-commit]') : null;
      if (pressed) {
        this.dropPendingPick();
        return;
      }
      var key = this.currentQueryKey();
      if (this.debouncePending) {
        // Looked up now (typing already dropped whatever was on its way); with too little input left,
        // this clears the list instead.
        clearTimeout(this.debounceTimer);
        this.debouncePending = false;
        if (key) this.startSettling();
        this.runSuggest();
        return;
      }
      if (key && this.suggestions.length && this.listKey === key) {
        this.startSettling();
        this.commitBestMatch();
        return;
      }
      if (key && this.lastSentQuery === key && this.listKey !== key) {
        this.startSettling();
      }
    },
    isEmptyAdditionField(element) {
      return !!element && !!element.getAttribute && element.getAttribute('name') === 'building_uid_appendix'
        && String(element.value || '').trim() === '' && String(this.addition || '').trim() === '';
    },
    // Tab from the house number into an empty addition: a list for the fields as they are commits now
    // when it holds exactly the typed plain house; a list still waiting in the debounce is asked at once,
    // and like one already on its way, decides when it lands (handleSuggestResult). A list with
    // variants picks nothing: the guest chooses or types an addition (the Tab itself closed the list).
    commitPlainHouse() {
      var key = this.currentQueryKey();
      if (!key || key.indexOf('address:') !== 0) {
        return;
      }
      // Settling while an answer that may commit is on its way, so the parent waits with saving; the
      // answer ends it, with a pick or with the list.
      if (this.debouncePending) {
        clearTimeout(this.debounceTimer);
        this.debouncePending = false;
        this.plainHouseOnArrival = true;
        this.startSettling();
        this.runSuggest();
        return;
      }
      if (this.suggestions.length && this.listKey === key) {
        if (this.listMode === 'address' && isExactPlainHouse(this.suggestions, this.houseNumber, this.addition, this.normalizedPostcode)) {
          this.startSettling();
          this.commitBestMatch();
        }
        return;
      }
      // Only while the answer for these fields is still on its way (once it landed, nothing will come).
      if (this.lastSentQuery === key && this.listKey !== key) {
        this.plainHouseOnArrival = true;
        this.startSettling();
      }
    },
    // commitOnBlur only: the parent hears that a pick made on leaving the fields is on its way
    // (settling true) and when it landed or failed (false), so it can wait with saving the form.
    // Eight seconds at most, so a lost answer never holds the parent.
    startSettling() {
      clearTimeout(this.settlingTimer);
      this.settlingTimer = setTimeout(this.endSettling, 8000);
      if (!this.settling) {
        this.settling = true;
        this.$emit('settling', true);
      }
    },
    endSettling() {
      clearTimeout(this.settlingTimer);
      if (this.settling) {
        this.settling = false;
        this.$emit('settling', false);
      }
    },
    // commitOnBlur: the guest went to a control that takes over from the lookup. A search waiting or on
    // its way and a pick on its way are dropped, and the list closes.
    dropPendingPick() {
      clearTimeout(this.debounceTimer);
      this.debouncePending = false;
      this.requestToken += 1;
      if (this.abortController) this.abortController.abort();
      this.lastSentQuery = null;
      this.resolveToken += 1;
      this.plainHouseOnArrival = false;
      this.showList = false;
      this.suggestions = [];
      this.listKey = null;
      this.endSettling();
    },
    commitBestMatch() {
      var index = this.bestMatchIndex();
      if (index >= 0) {
        this.selectCandidate(this.suggestions[index]);
      }
    },
    // The suggestion with exactly the typed house number and addition; else the highlighted one;
    // else the first.
    bestMatchIndex() {
      if (!this.suggestions.length) {
        return -1;
      }
      var typed = (this.listMode || this.activeMode) === 'address' ? typedHouseNumber(this.houseNumber, this.addition) : null;
      if (typed) {
        for (var i = 0; i < this.suggestions.length; i++) {
          var label = labelHouseNumber(this.suggestions[i].label);
          if (label && label.number === typed.number && label.rest === typed.rest) {
            return i;
          }
        }
      }
      return this.highlightedIndex >= 0 ? this.highlightedIndex : 0;
    },
    selectCandidate(candidate) {
      this.showList = false;
      this.suggestions = [];
      this.message = null;
      // commitOnBlur: every pick on its way counts as settling (also a single suggestion picked by
      // itself), so the parent waits with saving until it landed.
      if (this.commitOnBlur) this.startSettling();
      // commitOnBlur: the mode of the list the candidate came from, not of a newer request that is still
      // on its way. Without it (add-house, the admin) the mode as before.
      if (((this.commitOnBlur && this.listMode) || this.activeMode) === 'postcode') {
        this.runResolvePostcode(candidate.id);
        return;
      }
      this.runResolve(candidate.id);
    },
    runResolve(pdokId) {
      var self = this;
      var token = ++this.resolveToken;

      fetch(this.resolveUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ pdok_id: pdokId }),
      }).then(function (response) {
        return response.json().then(function (body) {
          return { httpStatus: response.status, body: body };
        });
      }).then(function (result) {
        // commitOnBlur: a newer pick, or the guest typing again, replaced this one.
        if (self.commitOnBlur && token !== self.resolveToken) {
          return;
        }
        if (result.httpStatus === 422) {
          // A parent that already has street and city needs no "enter it manually".
          self.message = self.addressFilled ? null : self.labels.invalidSelection;
          self.endSettling();
          return;
        }
        if (result.httpStatus !== 200 || !result.body || result.body.status !== 'ok' || !Array.isArray(result.body.data) || !result.body.data[0]) {
          self.markUnavailable();
          return;
        }

        var resolved = result.body.data[0];
        self.selectedPdokId = pdokId;
        self.applyingFill = true;
        self.$emit('resolved', resolved);
        self.endSettling();
        self.$nextTick(function () {
          self.applyingFill = false;
        });
      }).catch(function () {
        if (self.commitOnBlur && token !== self.resolveToken) {
          return;
        }
        self.markUnavailable();
      });
    },
    // Postcode-only counterpart of runResolve() - a DIFFERENT endpoint and a DIFFERENT event
    // ('resolved-postcode', never 'resolved') on purpose: the payload has no building_uid to
    // report and must never be handled by exact-address code that would fill a house number or
    // trigger BAG/energy-label enrichment from it.
    runResolvePostcode(pdokId) {
      var self = this;
      var token = ++this.resolveToken;

      fetch(this.resolvePostcodeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ pdok_id: pdokId }),
      }).then(function (response) {
        return response.json().then(function (body) {
          return { httpStatus: response.status, body: body };
        });
      }).then(function (result) {
        // commitOnBlur: a newer pick, or the guest typing again, replaced this one.
        if (self.commitOnBlur && token !== self.resolveToken) {
          return;
        }
        if (result.httpStatus === 422) {
          // A parent that already has street and city needs no "enter it manually".
          self.message = self.addressFilled ? null : self.labels.invalidSelection;
          self.endSettling();
          return;
        }
        if (result.httpStatus !== 200 || !result.body || result.body.status !== 'ok' || !Array.isArray(result.body.data) || !result.body.data[0]) {
          self.markUnavailable();
          return;
        }

        var resolved = result.body.data[0];
        self.selectedPdokId = pdokId;
        self.applyingFill = true;
        self.$emit('resolved-postcode', resolved);
        self.endSettling();
        self.$nextTick(function () {
          self.applyingFill = false;
        });
      }).catch(function () {
        if (self.commitOnBlur && token !== self.resolveToken) {
          return;
        }
        self.markUnavailable();
      });
    },
    onDocumentKeydown(event) {
      if (!this.showList || !this.suggestions.length) {
        return;
      }
      var active = document.activeElement;
      var activeName = active && active.getAttribute ? active.getAttribute('name') : null;
      if (['postcode', 'building_uid', 'building_uid_appendix'].indexOf(activeName) === -1) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestions.length;
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.highlightedIndex = this.highlightedIndex <= 0
            ? this.suggestions.length - 1
            : this.highlightedIndex - 1;
          break;
        case 'Enter':
          if (this.highlightedIndex >= 0) {
            event.preventDefault();
            this.selectCandidate(this.suggestions[this.highlightedIndex]);
          }
          break;
        case 'Escape':
          this.showList = false;
          this.suggestions = [];
          break;
        case 'Tab':
          // Tab must keep moving focus normally - only close the list, never select or block it.
          this.showList = false;
          break;
      }
    },
  },
};
</script>
